import bbApi from "../../api"
import api from "./axiosApi"
import { SearchResult, LoanOfficer } from "../../types"

// Context passed to handlers that need role-based filtering
export interface SearchContext {
  isLoanOfficer: boolean
  isSuperAdmin: boolean
  isBankAdmin: boolean
  userId?: string
  orgId?: string
}

// Each prefix handler takes a value and optional context
type PrefixHandler = (
  value: string,
  context?: SearchContext
) => Promise<SearchResult[]>

interface PrefixConfig {
  handler: PrefixHandler
  description: string // For future autocomplete hints
}

// ============================================
// PREFIX REGISTRY - Add new prefixes here
// ============================================
export const SEARCH_PREFIX_CONFIG: Record<string, PrefixConfig> = {
  loid: {
    description: "Search by Loan Officer ID",
    handler: async (loid, context) => {
      let url = `/api/v1/members?loid=${loid}`
      if (context?.isBankAdmin && !context.isSuperAdmin && context.orgId) {
        url += `&org_id=${context.orgId}`
      }
      const { data } = await api.get<{ lo?: LoanOfficer }>(url)
      if (!data.lo) return []
      const lo = data.lo
      return [
        {
          id: lo.uid || lo.loid || "",
          group_id: lo.bank_nid,
          item: `${lo.first_name} ${lo.last_name}`,
          type: "loan_officer"
        }
      ]
    }
  },

  brand_id: {
    description: "Search by Brand ID",
    handler: async (brandId, context) => {
      let url = `/bank/manage.json?id=${brandId}`
      if (context?.isBankAdmin && !context.isSuperAdmin && context.orgId) {
        url += `&org_id=${context.orgId}`
      }
      const { data } = await api.get<{
        id?: string
        name?: string
        org_id?: string
      }>(url)
      if (!data.id) return []
      return [
        {
          id: data.id,
          group_id: data.org_id || "",
          item: data.name || "",
          type: "brand"
        }
      ]
    }
  },

  nmls: {
    description: "Search Loan Officers by NMLS",
    handler: async (nmls, context) => {
      let url = `/api/v1/members?nmls=${nmls}`
      if (context?.isBankAdmin && !context.isSuperAdmin && context.orgId) {
        url += `&org_id=${context.orgId}`
      }
      const { data } = await api.get<{
        members?: LoanOfficer[]
        lo?: LoanOfficer
      }>(url)
      const members = data.members || (data.lo ? [data.lo] : [])
      return members.map((lo: LoanOfficer) => ({
        id: lo.uid || lo.loid || "",
        group_id: lo.bank_nid,
        item: `${lo.first_name} ${lo.last_name}`,
        type: "loan_officer" as const
      }))
    }
  },

  app_key: {
    description: "Search by App Key",
    handler: async (appKey, context) => {
      const params: Record<string, string> = { app_key: appKey }
      if (context?.isBankAdmin && !context.isSuperAdmin && context.orgId) {
        params.org_id = context.orgId
      }
      const { data } = await api.get<{
        results?: { id: string; title: string; brand_id: string }[]
      }>("/api/v1/integrations", { params })
      if (!data.results?.length) return []
      const integration = data.results[0]
      return [
        {
          id: integration.id,
          group_id: integration.brand_id || "",
          item: integration.title || `Integration: ${appKey}`,
          type: "integration"
        }
      ]
    }
  }

  // To add a new prefix, simply add another entry:
  // new_prefix: {
  //   description: "Search by X",
  //   handler: async (value) => { ... return SearchResult[] }
  // }
}

// Derived list of valid prefixes (auto-updates when config changes)
export const SEARCH_PREFIXES = Object.keys(SEARCH_PREFIX_CONFIG)

// Parse query and detect prefix
export interface ParsedQuery {
  prefix: string | null
  value: string
}

export function parseSearchQuery(query: string): ParsedQuery {
  const match = query.match(/^(\w+):(.*)$/)
  if (match && SEARCH_PREFIXES.includes(match[1])) {
    return { prefix: match[1], value: match[2].trim() }
  }
  return { prefix: null, value: query }
}

// Execute prefix search
export async function searchByPrefix(
  prefix: string,
  value: string,
  context?: SearchContext
): Promise<SearchResult[]> {
  const config = SEARCH_PREFIX_CONFIG[prefix]
  if (!config) return []
  return config.handler(value, context)
}
